import React, { useEffect } from 'react';
import { TabList, TabPanel, TabPanelBody, Tabs } from 'components/tabs';
import { generatePath, useParams } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import TabLink from 'components/tabs/TabLink';
import { useAbility } from '@casl/react';
import { AbilityContext } from 'Can';
import Badge from 'components/badge/Badge';
import {
  ROUTE_PERSONS_ENTITY_PERSON,
  ROUTE_PERSONS_ENTITY_PERSON_DOCS,
  ROUTE_PERSONS_ENTITY_PERSON_GLOBAL,
  ROUTE_PERSONS_ENTITY_PERSON_GLOBAL_EDIT,
  ROUTE_PERSONS_ENTITY_PERSON_GROUPS,
  ROUTE_PERSONS_ENTITY_PERSON_INPUT_DATA,
  ROUTE_PERSONS_ENTITY_PERSON_MEDICAL,
  ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_ILLNESS_DETAIL,
  ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_ILLNESS_DETAIL_EDIT,
  ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_DETAIL,
  ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_DETAIL_EDIT,
  ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS,
  ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_ISSUE_DETAIL,
  ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_ISSUE_DETAIL_EDIT,
  ROUTE_PERSONS_ENTITY_PERSON_PROGRAMS,
  ROUTE_PERSONS_ENTITY_PERSON_PROGRAMS_ENTITY_PROGRAM,
  ROUTE_PERSONS_ENTITY_PERSON_PROGRAMS_ENTITY_PROGRAM_FEEDBACK,
  ROUTE_PERSONS_ENTITY_PERSON_VIDEOS,
  ROUTE_PERSONS_ENTITY_PERSON_VIDEOS_VIDEO
} from 'routes/RouteList';
import useTabIndex from 'hooks/utils/useTabIndex';
import useSessionCount from 'hooks/queries/useSessionCount';
import messages from 'messages';
import PersonSessionsTab from 'containers/pages/persons/PersonSessionsTab';
import PersonMedicalTab from 'containers/pages/persons/PersonMedicalTab';
import PersonVideosTab from 'containers/pages/persons/PersonVideosTab';
import PersonProgramsTab from 'containers/pages/persons/PersonProgramsTab';
import PersonGroupsTab from 'containers/pages/persons/PersonGroupsTab';
import PersonDocsTab from 'containers/pages/persons/PersonDocsTab';
import PersonGlobalTab from 'containers/pages/persons/PersonGlobalTab';
import PersonInputDataTab from 'containers/pages/persons/PersonInputDataTab';

const PersonTabs = () => {
  const { entityId, groupId, personId } = useParams();
  const ability = useAbility(AbilityContext);
  const { totalSessions, getSessionCount } = useSessionCount();

  // Define the tabs for the logged in user
  let routes = [];
  if (ability.can('read', 'Sessions')) {
    routes.push(ROUTE_PERSONS_ENTITY_PERSON);
  }

  if (ability.can('read', 'Rehab')) {
    routes.push([
      ROUTE_PERSONS_ENTITY_PERSON_MEDICAL,
      ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_DETAIL,
      ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_DETAIL_EDIT,
      ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_ILLNESS_DETAIL,
      ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_ILLNESS_DETAIL_EDIT,
      ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_ISSUE_DETAIL,
      ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_ISSUE_DETAIL_EDIT,
      ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS
    ]);
  }

  if (ability.can('manage', 'Videos')) {
    routes = [
      [
        ROUTE_PERSONS_ENTITY_PERSON,
        ROUTE_PERSONS_ENTITY_PERSON_VIDEOS,
        ROUTE_PERSONS_ENTITY_PERSON_VIDEOS_VIDEO
      ]
    ];
  }

  if (ability.can('read', 'Programs')) {
    routes.push([
      ROUTE_PERSONS_ENTITY_PERSON_PROGRAMS,
      ROUTE_PERSONS_ENTITY_PERSON_PROGRAMS_ENTITY_PROGRAM,
      ROUTE_PERSONS_ENTITY_PERSON_PROGRAMS_ENTITY_PROGRAM_FEEDBACK
    ]);
  }

  // No trainings view for now
  /*if (ability.can('read', 'Trainings')) {
    routes.push([ROUTE_PERSONS_ENTITY_PERSON_TRAININGS]);
  }*/

  if (ability.can('manage', 'Persons')) {
    routes.push([ROUTE_PERSONS_ENTITY_PERSON_GROUPS]);
  }
  if (ability.can('read', 'Documents')) {
    routes.push([ROUTE_PERSONS_ENTITY_PERSON_DOCS]);
  }

  const personRoutes = [];
  if (ability.can('manage', 'Persons')) {
    personRoutes.push(ROUTE_PERSONS_ENTITY_PERSON_GLOBAL_EDIT);
  }
  if (ability.can('read', 'Persons')) {
    personRoutes.push(ROUTE_PERSONS_ENTITY_PERSON_GLOBAL);
  }
  routes.push(personRoutes);

  if (ability.can('read', 'GrowthTracker_v2')) {
    routes.push(ROUTE_PERSONS_ENTITY_PERSON_INPUT_DATA);
  }

  const [tabIndex, setTabIndex] = useTabIndex(routes);

  useEffect(() => {
    if (personId) {
      getSessionCount(entityId, personId);
    }
  }, [personId]);

  return (
    <Tabs selectedIndex={tabIndex} onSelect={index => setTabIndex(index)}>
      <TabList>
        {ability.can('read', 'Sessions') && (
          <TabLink
            to={generatePath(ROUTE_PERSONS_ENTITY_PERSON, {
              entityId,
              groupId,
              personId
            })}
            dataQa={'sporter-sessions'}
          >
            {totalSessions > 0 && <Badge>{totalSessions}</Badge>}
            <FormattedMessage {...messages.tabSporterSession} />
          </TabLink>
        )}

        {ability.can('read', 'Rehab') && (
          <TabLink
            to={generatePath(ROUTE_PERSONS_ENTITY_PERSON_MEDICAL, {
              entityId,
              groupId,
              personId
            })}
            dataQa={'sporter-medical'}
          >
            <FormattedMessage {...messages.tabSporterMedical} />
          </TabLink>
        )}

        {ability.can('manage', 'Videos') && (
          <TabLink
            to={generatePath(ROUTE_PERSONS_ENTITY_PERSON_VIDEOS, {
              entityId,
              groupId,
              personId
            })}
            dataQa={'sporter-videos'}
          >
            <FormattedMessage {...messages.tabSporterVideos} />
          </TabLink>
        )}

        {ability.can('read', 'Programs') && (
          <TabLink
            to={generatePath(ROUTE_PERSONS_ENTITY_PERSON_PROGRAMS, {
              entityId,
              groupId,
              personId
            })}
            dataQa={'sporter-programs'}
          >
            <FormattedMessage {...messages.programs} />
          </TabLink>
        )}

        {/*
                  // No trainings view for now
                  {ability.can('read', 'Trainings') && (
                    <TabLink
                      to={generatePath(
                        ROUTE_PERSONS_ENTITY_PERSON_TRAININGS,
                        {
                          entityId,
                          groupId,
                          personId
                        }
                      )}
                      dataQa={'sporter-trainings'}
                    >
                      <FormattedMessage {...messages.trainingsTitle} />
                    </TabLink>
                  )}*/}

        {ability.can('manage', 'Persons') && (
          <TabLink
            to={generatePath(ROUTE_PERSONS_ENTITY_PERSON_GROUPS, {
              entityId,
              groupId,
              personId
            })}
          >
            <FormattedMessage {...messages.athleteGroups} />
          </TabLink>
        )}

        {ability.can('read', 'Documents') && (
          <TabLink
            to={generatePath(ROUTE_PERSONS_ENTITY_PERSON_DOCS, {
              entityId,
              groupId,
              personId
            })}
            dataQa={'sporter-docs'}
          >
            <FormattedMessage {...messages.athleteDocs} />
          </TabLink>
        )}

        {/* Person Global */}
        <TabLink
          to={generatePath(ROUTE_PERSONS_ENTITY_PERSON_GLOBAL, {
            entityId,
            groupId,
            personId
          })}
          dataQa={'sporter-global'}
        >
          <FormattedMessage {...messages.tabSporterGlobal} />
        </TabLink>

        {/* Person Input Data */}
        {ability.can('read', 'GrowthTracker_v2') && (
          <TabLink
            to={generatePath(ROUTE_PERSONS_ENTITY_PERSON_INPUT_DATA, {
              entityId,
              groupId,
              personId
            })}
            dataQa={'sporter-input-data'}
          >
            <FormattedMessage {...messages.tabSporterInputData} />
          </TabLink>
        )}
      </TabList>

      {ability.can('read', 'Sessions') && (
        <TabPanel>
          <TabPanelBody>
            <PersonSessionsTab />
          </TabPanelBody>
        </TabPanel>
      )}
      {ability.can('read', 'Rehab') && (
        <TabPanel>
          <TabPanelBody withScroller>
            <PersonMedicalTab />
          </TabPanelBody>
        </TabPanel>
      )}
      {ability.can('manage', 'Videos') && (
        <TabPanel>
          <TabPanelBody withScroller>
            <PersonVideosTab />
          </TabPanelBody>
        </TabPanel>
      )}
      {ability.can('read', 'Programs') && (
        <TabPanel>
          <TabPanelBody>
            <PersonProgramsTab />
          </TabPanelBody>
        </TabPanel>
      )}
      {/*
                // No trainings view for now
                {ability.can('read', 'Trainings') && (
                  <TabPanel>
                    <TabPanelBody>
                      <PersonTrainings
                        entityId={rootEntityId}
                        personId={personId}
                      />
                    </TabPanelBody>
                  </TabPanel>
                )}*/}
      {ability.can('manage', 'Persons') && (
        <TabPanel>
          <TabPanelBody>
            <PersonGroupsTab />
          </TabPanelBody>
        </TabPanel>
      )}
      {ability.can('read', 'Documents') && (
        <TabPanel>
          <TabPanelBody>
            <PersonDocsTab />
          </TabPanelBody>
        </TabPanel>
      )}
      {/* Person Global */}
      <TabPanel>
        <TabPanelBody withScroller>
          <PersonGlobalTab />
        </TabPanelBody>
      </TabPanel>

      {ability.can('read', 'GrowthTracker_v2') && (
        <TabPanel>
          <TabPanelBody withScroller>
            <PersonInputDataTab />
          </TabPanelBody>
        </TabPanel>
      )}
    </Tabs>
  );
};

export default PersonTabs;
